import { stopReplacedDocument } from "./import_reload.js";
import React, { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WebsocketProvider } from "y-websocket";
import { v4 as uuidv4 } from "uuid";
import { TechPackDoc, serializeTechPackCollections } from "../schema.js";
import { StudioProvider } from "./StudioContext.js";
import { getFreshToken, refreshTokenOrClear, supabase } from "./auth.js";
import TechPackForm from "./TechPackForm.js";
import AwarenessPanel from "./AwarenessPanel.js";
import { WorkspaceProvider } from "./WorkspaceProvider.js";
import { ActiveObjectProvider, useActiveObject } from "./ActiveObjectProvider.js";
import { ActivityProvider } from "./ActivityProvider.js";
import { FileAssetListener } from "./FileAssetListener.js";
import { ThreadProvider } from "./ThreadProvider.js";
import { DegradedModeBanner } from "./DegradedModeBanner.js";
import { FieldPatchQueue } from "./FieldPatchQueue.js";
import {
  getOrCreate, colorForId, deriveDisplayName,
  LANG_KEY, WS_BASE, LANGS, LANG_LABELS, F, BTN, ui,
  type Lang, type DocStatus, type TechPackEntry,
} from "./shared.js";
import { AccountSidebar } from "./AccountSidebar.js";
import { ChatPanel } from "./ChatPanel.js";
import { FileList } from "./LandingPage.js";
import { type FolderEntry } from "./pack_folder_list.js";
import { EditorHeader } from "./EditorPage.js";
import { AccessDenialToast, notifyDenied } from "./access_denial_toast.js";

// ---------------------------------------------------------------------------
// ErrorBoundary
// ---------------------------------------------------------------------------

interface EBState { error: Error | null }
export class ErrorBoundary extends Component<{ children: React.ReactNode }, EBState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error): EBState { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "40px", background: "#000", color: "#fff", fontFamily: "Space Mono, monospace", fontSize: "12px" }}>
          <div style={{ color: "#e05555", marginBottom: "16px" }}>interface error</div>
          <div style={{ color: "#666", marginBottom: "24px" }}>{this.state.error.message}</div>
          <button onClick={() => this.setState({ error: null })} style={{ background: "#fff", color: "#000", border: "none", padding: "8px 16px", cursor: "pointer", fontFamily: "Space Mono, monospace", marginRight: "12px" }}>Retry</button>
          <button onClick={() => { window.location.href = "/"; }} style={{ background: "transparent", color: "#666", border: "1px solid #333", padding: "8px 16px", cursor: "pointer", fontFamily: "Space Mono, monospace" }}>Home</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// TopBar
// ---------------------------------------------------------------------------

interface TopBarProps {
  lang: Lang; onLangChange: (l: Lang) => void; onAvatarClick: () => void;
  email: string; userColor: string; avatarUrl: string; displayName: string;
}
function TopBar({ lang, onLangChange, onAvatarClick, email, userColor, avatarUrl, displayName }: TopBarProps) {
  const initials = displayName.slice(0, 2).toUpperCase();
  return (
    <header style={{ display:"flex", alignItems:"center", justifyContent:"space-between", height:48, padding:"0 20px", borderBottom:"1px solid var(--border)", background:"var(--bg)", flexShrink:0 }}>
      <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"1rem", fontWeight:"normal", letterSpacing:"0.2em", textTransform:"uppercase" }}>
        LANFINITAS <span style={{ color:"var(--text-muted)" }}>STUDIO</span>
      </span>
      <div style={{ display:"flex", alignItems:"center", gap:2 }}>
        {LANGS.map((l, i) => (
          <span key={l} style={{ display:"contents" }}>
            <button onClick={() => onLangChange(l)} style={{ ...F, background:"transparent", border:"none", color: lang===l ? "var(--text)" : "var(--text-muted)", fontSize:10, fontWeight: lang===l ? 700 : 400, letterSpacing:"0.1em", padding:"4px 5px", cursor:"pointer" }}>{LANG_LABELS[l]}</button>
            {i < LANGS.length-1 && <span style={{ color:"#333", fontSize:10 }}>·</span>}
          </span>
        ))}
        <button onClick={onAvatarClick} title={email} style={{ width:28, height:28, borderRadius:"50%", background: avatarUrl ? "transparent" : userColor, color:"#0d0d0d", border:"none", fontSize:10, fontWeight:700, cursor:"pointer", flexShrink:0, marginLeft:10, ...F, padding:0, overflow:"hidden" }}>
          {avatarUrl ? <img src={avatarUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:"50%", display:"block" }} /> : initials}
        </button>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Session-expired toast (Bug F). Shown briefly before bouncing to login when a
// WS auth-close (4001) is followed by a failed token refresh = the session is
// truly dead. Reassures the user their work is safe: edits sync to the server
// live over the WS (server persists doc_yjs on every update) and via the 1 s
// REST autosave, so everything up to the disconnect is already saved.
// ---------------------------------------------------------------------------

function SessionExpiredToast() {
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 10000,
      background: "#1a0f00", borderBottom: "1px solid #7c4a00",
      color: "#f0a040", padding: "8px 16px", fontSize: 12,
      fontFamily: "Space Mono, monospace", textAlign: "center",
    }}>
      ⚠ Session expired — your changes are saved. Please sign in again…
    </div>
  );
}

// ---------------------------------------------------------------------------
// AppInner
// ---------------------------------------------------------------------------

function AppInner() {
  const userId    = useMemo(() => getOrCreate("studio_user_id",   uuidv4), []);
  const userName  = useMemo(() => getOrCreate("studio_user_name", () => "User-"+userId.slice(0,4).toUpperCase()), [userId]);
  const email     = localStorage.getItem("studio_email") ?? userId;
  const userColor = useMemo(() => colorForId(userId), [userId]);

  const [avatarUrl,    setAvatarUrl]    = useState(() => localStorage.getItem("studio_avatar") ?? "");
  const [displayName,  setDisplayName]  = useState(() => deriveDisplayName(email, localStorage.getItem("studio_nickname") ?? ""));
  const [lang,         setLangState]    = useState<Lang>((localStorage.getItem(LANG_KEY) as Lang) ?? "en");
  const [packs,        setPacks]        = useState<TechPackEntry[]>([]);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [initialFolders, setInitialFolders] = useState<FolderEntry[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState<TechPackEntry|null>(null);
  const [pendingPack,  setPendingPack]  = useState<TechPackEntry|null>(null);
  const [conn,         setConn]         = useState<{ techPack: TechPackDoc; provider: WebsocketProvider }|null>(null);
  const [accountOpen,  setAccountOpen]  = useState(false);
  const [creditsUsed,  setCreditsUsed]  = useState(() => parseInt(localStorage.getItem("studio_credits_used") ?? "0", 10));
  // Folder-service-level Activity Log (P2a-3/V1): the active shared folder whose
  // folder-level activity the landing ChatPanel shows. Lives in App (not FileList,
  // which remounts on editor enter/exit) so the selection survives opening/closing
  // a Tech Pack. Defaults to the first shared folder until the user picks another.
  const [activeFolderId, setActiveFolderId] = useState<string | null>(() => localStorage.getItem("studio_active_folder"));
  const connRef = useRef<{ provider: WebsocketProvider }|null>(null);
  // Snapshot of shared-with-me item ids from the previous refreshLists — drives
  // the list-level revocation/removal notice (null until the first fetch lands,
  // so the initial load never false-fires).
  const prevSharedRef = useRef<{ folders: Set<string>; packs: Set<string> } | null>(null);
  const creatingPackRef = useRef(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setActiveStyleId } = useActiveObject();

  // Keep the active shared folder STABLE and always valid (Surface Activity is
  // scoped to it; it must not reset/disappear across navigation). Wait for folders
  // to load, then: if the current value is still a shared folder, keep it; else
  // fall back to the first shared folder. Persisted so it survives reload.
  useEffect(() => {
    if (initialFolders.length === 0) return;            // not loaded — keep restored value
    const shared = initialFolders.filter(f => f.is_shared);
    if (activeFolderId && shared.some(f => f.id === activeFolderId)) return;
    setActiveFolderId(shared[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFolders]);
  useEffect(() => {
    if (activeFolderId) { try { localStorage.setItem("studio_active_folder", activeFolderId); } catch { /**/ } }
  }, [activeFolderId]);

  // BUGFIX (cold-bootstrap duplicate requests, 2026-09-22): on a COLD reload
  // (post-login window.location.reload()/href navigation, per AccountSidebar
  // sign-out and main.tsx postAuthNav), the browser fires both a `focus` and
  // a `visibilitychange` event as the freshly-loaded tab becomes active --
  // in quick succession, sometimes on the very same tick. Both listeners
  // below called refreshLists() independently, on top of the mount-time
  // call, with NO de-dup guard: up to 3 concurrent refreshLists() calls,
  // each firing its own Promise.all([techpacks, folders]) pair (2 DB
  // queries), landing at the exact moment profile load / document load / WS
  // connect (packAccess + room DB read) are ALSO in flight for the same
  // fresh session -- 6+ concurrent DB queries competing for the pool within
  // the same ~100-300ms window, right when the pool has zero warm/idle
  // connections yet (cold). A WARM in-session reload/reopen never hits this:
  // no mount-time refreshLists, no focus/visibilitychange coincidence, only
  // the single document GET + WS connect fire. This does not by itself
  // require a connection LEAK (none found — see server.ts's Pool hardening
  // commit) to explain a transient 500; it only requires enough concurrent
  // acquisitions to occasionally lose the race against Postgres accepting
  // that many new connections within the timeout window, especially right
  // after the pool was fully cold (e.g. the previous session's connections
  // had already gone idle-and-closed).
  //
  // Fix: collapse concurrent refreshLists() callers onto ONE in-flight
  // request via a ref-held promise, instead of each firing its own pair.
  const inFlightRefreshRef = useRef<Promise<void> | null>(null);

  // Fetch the pack list + folders from DB and push them into state. Extracted so
  // it can be re-run on navigation (goBack) and on tab refocus — the landing list
  // is otherwise fetched only once at mount and goes stale (E-3): newly-created
  // items, a collaborator's edits, and newly-shared folders don't show until a
  // manual refresh. On a dead session we surface sessionExpired and KEEP prior
  // state rather than clobbering the list with [] (the "data disappeared" trap).
  const refreshLists = useCallback(async () => {
    if (inFlightRefreshRef.current) return inFlightRefreshRef.current;
    const run = (async () => {
      let token = await getFreshToken();
      if (!token) token = await refreshTokenOrClear();
      if (!token) { setSessionExpired(true); return; }
      const auth = { Authorization: `Bearer ${token}` };
      let packsRes: Response | null = null, foldersRes: Response | null = null;
      try {
        [packsRes, foldersRes] = await Promise.all([
          fetch("/api/studio/techpacks", { headers: auth }),
          fetch("/api/studio/folders",   { headers: auth }),
        ]);
      } catch { return; /* network blip — keep prior state, do not clobber */ }
      if (packsRes?.status === 401 || foldersRes?.status === 401) { setSessionExpired(true); return; }
      const newPacks   = packsRes?.ok   ? (await packsRes.json().catch(() => [])   as TechPackEntry[]) : null;
      const newFolders = foldersRes?.ok ? (await foldersRes.json().catch(() => []) as FolderEntry[])   : null;
      if (newPacks)   setPacks(newPacks);
      if (newFolders) setInitialFolders(newFolders);
      // List-level membership notice (founder, 2026-06-13 corrections). ONLY a
      // whole-FOLDER disappearance (real kick / unshare = membership loss) raises a
      // popup here. A single shared PACK vanishing does NOT — a bystander who merely
      // had the pack visible in the list (but was working in a different pack) must
      // not be alarmed; whoever was actually IN the removed pack is told via the
      // open-TP path (WS close code / autosave reason) with the correct pack-level
      // message. This kills the bystander-pack noise and the double-notice.
      if (newPacks && newFolders) {
        const sharedFolders = new Set(newFolders.filter(f => f.is_owner === false).map(f => f.id));
        const sharedPacks   = new Set(newPacks.filter(p => p.isOwner === false).map(p => p.id));
        const prev = prevSharedRef.current;
        if (prev) {
          const lostFolder = [...prev.folders].some(id => !sharedFolders.has(id));
          if (lostFolder) notifyDenied("denial_access_revoked");
        }
        prevSharedRef.current = { folders: sharedFolders, packs: sharedPacks };
      }
    })();
    inFlightRefreshRef.current = run;
    try {
      await run;
    } finally {
      inFlightRefreshRef.current = null;
    }
  }, []);

  // Initial load — both lists must resolve before the landing renders (avoids a
  // flash of packs at root before their folders arrive). packsLoading clears
  // once the first fetch settles.
  useEffect(() => {
    let cancelled = false;
    (async () => { await refreshLists(); if (!cancelled) setPacksLoading(false); })();
    return () => { cancelled = true; };
  }, [refreshLists]);

  // E-3: keep the landing list fresh. Re-fetch when the user returns to the
  // tab/window — a collaborator's changes then show on refocus (the basic
  // expectation). Navigation events refetch directly (goBack → refreshLists()).
  // De-duplicated against the mount-time call and each other via
  // inFlightRefreshRef (see refreshLists) so a cold-reload's near-simultaneous
  // focus + visibilitychange events collapse onto one request instead of
  // stacking three.
  useEffect(() => {
    const onFocus = () => { void refreshLists(); };
    const onVis   = () => { if (!document.hidden) void refreshLists(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refreshLists]);

  useEffect(() => {
    const token = localStorage.getItem("studio_token") ?? "";
    if (!token) return;
    fetch("/api/profile", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then((p: Record<string, unknown> | null) => {
        if (!p) return;
        if (typeof p.language === "string") { setLangState(p.language as Lang); localStorage.setItem(LANG_KEY, p.language); }
        if (typeof p.display_name === "string" && p.display_name) setDisplayName(p.display_name);
        // credits_used is intentionally never returned by /api/profile — it's
        // local-only (see consumeCredit below); no read from the server here.
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const l = (e as CustomEvent).detail?.lang;
      if (l) setLangState(l as Lang);
    };
    window.addEventListener("languageChanged", handler);
    return () => window.removeEventListener("languageChanged", handler);
  }, []);

  function changeLang(l: Lang) {
    setLangState(l);
    localStorage.setItem(LANG_KEY, l);
    window.dispatchEvent(new CustomEvent("languageChanged", { detail: { lang: l } }));
    const token = localStorage.getItem("studio_token") ?? "";
    if (token) fetch("/api/profile", { method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ language: l }) }).catch(() => {});
  }

  function consumeCredit() {
    // credits_used is intentionally local-only (localStorage), not sent to
    // /api/profile: it was never a real server-enforced balance (Studio has
    // no credit-consumption gate), and the shared profile API's allow-list
    // (V1_SHARED_PROFILE_NORMALIZATION_PLAN.md) explicitly excludes any
    // billing/credit field, so this PATCH would only ever fail a 400 now.
    setCreditsUsed(prev => {
      const next = prev + 1;
      localStorage.setItem("studio_credits_used", String(next));
      return next;
    });
  }

  // Broadcast autosave status so EditorHeader can show a visible indicator.
  function emitSaveStatus(status: "saving" | "saved" | "error") {
    window.dispatchEvent(new CustomEvent("studio:save-status", { detail: { status } }));
  }

  async function autoSave(techPack: TechPackDoc, roomId: string): Promise<void> {
    // E-1 v2 persist guard: never persist a doc whose initial load hasn't settled
    // (hydrated) or failed (loadOk=false) — that would write an un-hydrated /
    // failed-load empty doc over real DB content. Once both are true, EVERY save
    // proceeds (incl. legitimately clearing a single field) — this never blocks a
    // genuine edit.
    if (!techPack.hydrated || !techPack.loadOk) {
      console.warn("[autoSave] skipped -- not hydrated / load failed (protecting DB)");
      return;
    }
    const token = await getFreshToken();
    if (!token) { console.warn("[autoSave] no token -- skipping"); emitSaveStatus("error"); return; }
    emitSaveStatus("saving");
    const payload = {
      techpackId:               roomId,
      userId,
      // G1: every TP collection is serialized from the canonical registry so
      // autosave can never omit one (this is what now persists `trim`). The two
      // non-form streams stay explicit.
      ...serializeTechPackCollections(techPack),
      activities:               techPack.ydoc.getArray("__studio_activity__").toArray(),
      chat_messages:            techPack.ydoc.getArray("__studio_chat__").toArray(),
    };
    try {
      const res = await fetch(`/api/studio/techpacks/${roomId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        emitSaveStatus("saved");
      } else {
        console.error("[autoSave] failed:", res.status);
        emitSaveStatus("error");
        // Founder's trigger-rule 3 fallback: B kept the editor open after the
        // owner revoked access / removed the pack, missed the list-level notice,
        // and tried to fill in data — the save failure is the moment to explain.
        // The server tags a 404 with a `reason` so this distinguishes a pack-only
        // loss from a membership loss (founder invariant, 2026-06-12).
        if (res.status === 404) {
          const reason = ((await res.json().catch(() => ({}))) as { reason?: string }).reason;
          notifyDenied(reason === "pack_access_lost" ? "denial_pack_moved_out"
                     : reason === "membership_revoked" ? "denial_access_revoked"
                     : "denial_pack_removed");
        } else if (res.status === 403) {
          notifyDenied("denial_access_revoked");
        }
      }
    } catch (err) {
      console.error("[autoSave] error:", err);
      emitSaveStatus("error");
    }
  }

  // Debounced autosave: a typing burst collapses to one PATCH (1s after the
  // last edit). Cuts cost (one PATCH = one credit) and avoids self-contention
  // from concurrent in-flight saves racing on the same row.
  function scheduleAutoSave(techPack: TechPackDoc, roomId: string) {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      void autoSave(techPack, roomId);
    }, 1000);
  }

  async function selectPack(pack: TechPackEntry) {
    setPendingPack(pack);
    // Flush any pending debounced save for the outgoing pack before switching.
    if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    if (conn && selectedPack) await autoSave(conn.techPack, selectedPack.id);
    connRef.current?.provider.destroy();
    const tp    = new TechPackDoc();
    const token = await getFreshToken();
    console.log("[StudioContext] WS connecting to:", WS_BASE, "roomId:", pack.id);
    const prov  = new WebsocketProvider(`${WS_BASE}`, pack.id, tp.ydoc, { params: { token } } as Parameters<typeof WebsocketProvider>[3]);
    // Bug F: y-websocket bakes the token into the URL once and reuses it on
    // every reconnect (the `url` getter reads `this.params` each setupWS). When
    // the session rotates/dies, that frozen token gets rejected (verifyJWT →
    // close 4001) and the client retries every ~3 s forever. On a 4001 only
    // (not transient network closes, nor #209's 4403 access-close), refresh the
    // token and swap it into params so the NEXT reconnect uses a live token. If
    // the refresh fails, the session is truly dead: stop the loop and bounce to
    // login. Other close codes fall through to y-websocket's normal reconnect.
    let reauthing = false;
    prov.on("connection-close", (event: { code?: number } | null) => {
      if (stopReplacedDocument(event?.code, tp, prov)) {
        // Do not use goBack(): it flushes the now-obsolete document.
        if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
        if (connRef.current?.provider === prov) {
          connRef.current = null; setConn(null); setSelectedPack(null); setPendingPack(null);
          setActiveStyleId(null); localStorage.removeItem("studio_active_pack_id");
          void refreshLists();
        }
        return;
      }
      // Reason-aware access close (founder invariant, 2026-06-12): the open-TP
      // warning MUST distinguish a pack-only loss from a membership loss.
      //   4403 → membership_revoked  ("no longer part of this shared group")
      //   4408 → pack_access_lost    ("no access to this pack, still on the team")
      //   4409 → pack_removed        ("this pack was removed by the owner")
      // Without this the socket also retries every ~3s forever; stop the loop,
      // show the correct message, and return to landing.
      if (event?.code === 4403 || event?.code === 4408 || event?.code === 4409) {
        prov.disconnect();
        notifyDenied(event.code === 4408 ? "denial_pack_moved_out"
                   : event.code === 4409 ? "denial_pack_removed"
                   : "denial_access_revoked");
        void goBack();
        return;
      }
      if (event?.code !== 4001 || reauthing) return;
      void (async () => {
        const fresh = await refreshTokenOrClear();
        if (fresh) { (prov as unknown as { params: Record<string, string> }).params.token = fresh; return; }
        reauthing = true;
        prov.disconnect();
        setSessionExpired(true);
        setTimeout(() => { void supabase.auth.signOut().catch(() => {}).finally(() => { window.location.href = "/"; }); }, 2500);
      })();
    });
    const c = { techPack: tp, provider: prov };
    // Origin-filtered autosave. `origin === prov` means a remote WS update —
    // either the server replaying the stored doc (hydration) or a collaborator's
    // edit. Both must NOT trigger a local save: hydration would risk overwriting
    // real data, and collaborator edits would cause N clients to PATCH the same
    // row (write contention + redundant credit cost). Only LOCAL edits (origin
    // null) reach autoSave — and they do so regardless of WS sync state, so
    // autosave works even when the WebSocket is slow or degraded.
    tp.ydoc.on("update", (_update: Uint8Array, origin: unknown) => {
      if (origin === prov || !pack?.id) return;
      scheduleAutoSave(tp, pack.id);
    });
    connRef.current = c; setConn(c); setSelectedPack(pack); setPendingPack(null);
    localStorage.setItem("studio_active_pack_id", pack.id);
    setPacks(prev => prev.map(p => p.id===pack.id ? { ...p, updatedAt: new Date().toISOString() } : p));
    setActiveStyleId(pack.id);
  }

  async function goBack() {
    setPendingPack(null);
    if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    if (conn && selectedPack) await autoSave(conn.techPack, selectedPack.id);
    connRef.current?.provider.destroy(); connRef.current = null; setConn(null); setSelectedPack(null);
    localStorage.removeItem("studio_active_pack_id");
    setActiveStyleId(null);
    // E-3: re-fetch on return to landing so the list reflects server truth —
    // this is what a manual refresh did, so it fixes the "shared folder vanishes
    // on Back" symptom by construction (refresh fixes it = refetch fixes it).
    void refreshLists();
  }

  function handleStatusChange(id: string, status: DocStatus) {
    setPacks(prev => prev.map(p => p.id===id ? { ...p, status } : p));
    setSelectedPack(prev => prev?.id===id ? { ...prev, status } : prev);
  }

  async function createNewPack() {
    if (creatingPackRef.current) return;
    creatingPackRef.current = true;
    try {
      const token = await getFreshToken();
      if (!token) return;
      const id = uuidv4();
      // IP boundary: never seed a UUID fragment into the human-facing
      // style_number (this was the "UUID flash" on new-pack return — the
      // NEW-<uuid6> name made the "(unnamed)" fallback unreachable). Leave it
      // empty: lists render the localized "(unnamed)" until the user names it.
      const styleNumber = "";
      // The server assigns the row's primary key (tech_packs.id) and returns it.
      // Previously this client kept its own `id` and ignored the response, so the
      // row lived under the server id while the WS room, autosave, folder-move
      // (PATCH /techpacks/:id/folder) and delete calls all targeted the client id
      // — which never existed in tech_packs → the 404 storm (Bug A). Anchor every
      // subsequent operation to the server-assigned id.
      let packId = id;
      try {
        const res = await fetch("/api/studio/techpacks", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ id, styleNumber, productCategory: "", season: "", status: "draft" })
        });
        if (!res.ok) { console.error("[createNewPack] POST failed:", res.status); return; }
        const created = await res.json().catch(() => ({})) as { id?: string };
        if (typeof created.id === "string" && created.id) packId = created.id;
      } catch (err) { console.error("[createNewPack] POST error:", err); return; }
      // Fail-closed guards (pack_folder_list) require explicit true — a locally
      // created pack is by definition the caller's own unfiled pack.
      const entry: TechPackEntry = { id: packId, styleNumber, productCategory: "", season: "", status: "draft", updatedAt: new Date().toISOString(), isOwner: true, isPackCreator: true };
      setPacks(prev => [entry, ...prev]);
      selectPack(entry);
    } finally {
      creatingPackRef.current = false;
    }
  }

  async function deletePack(id: string) {
    const token = await getFreshToken();
    if (!token) return;
    const res = await fetch(`/api/studio/techpacks/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => null);
    // UX-COPY-1: only remove on success. A collaborator's delete returns 403 —
    // don't optimistically vanish it (it would reappear on refetch); explain why.
    if (res && res.ok) { setPacks(prev => prev.filter(p => p.id !== id)); return; }
    // 403: either a plain member (no rights) or — Q2 — a folder owner trying to
    // delete a member's contribution (allowed action is EJECT, not delete). The
    // UI normally routes the latter to eject, so this is a defensive fallback.
    if (res && res.status === 403) { notifyDenied("denial_delete_owner_only"); void refreshLists(); return; }
    void refreshLists();  // 404 / network — re-sync to server truth
  }

  function importPack(entry: TechPackEntry) {
    // The import response may precede the WS close event. Never let selectPack
    // flush the outgoing generation over the replacement in that interval.
    if (selectedPack?.id === entry.id && conn) { conn.techPack.loadOk = false; conn.techPack.hydrated = false; }
    // Real SpecFlow import: the server already committed the pack (new or
    // re-imported). Merge the returned entry locally, re-sync from the server
    // (authoritative), then open the imported pack immediately.
    setPacks(prev => [entry, ...prev.filter(p => p.id !== entry.id)]);
    void refreshLists();
    void selectPack(entry);
  }

  useEffect(() => () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    connRef.current?.provider.destroy();
  }, []);

  useEffect(() => {
    const handler = () => { if (connRef.current && selectedPack) void autoSave(connRef.current.techPack, selectedPack.id); };
    window.addEventListener("studio:dataImported", handler);
    return () => window.removeEventListener("studio:dataImported", handler);
  }, [selectedPack]);

  // Restore the last-active pack after the pack list finishes loading.
  // One-shot: packsLoading flips true→false exactly once per mount.
  // The origin filter inside selectPack blocks autoSave on WS hydration
  // (origin === prov), so restore never overwrites real data with an empty doc.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (packsLoading) return;
    const savedId = localStorage.getItem("studio_active_pack_id");
    if (!savedId) return;
    const pack = packs.find(p => p.id === savedId);
    if (pack) void selectPack(pack);
  }, [packsLoading]); // intentional: one-shot on load-complete, packs/selectPack excluded

  useEffect(() => {
    function onSaved(e: Event) {
      const { id, styleNumber } = (e as CustomEvent).detail as { id: string; styleNumber: unknown };
      const sn = typeof styleNumber === "object" && styleNumber !== null
        ? (styleNumber as { value?: string }).value
        : styleNumber as string;
      if (!sn || !id) return;
      setPacks(prev => prev.map(p => p.id === id ? { ...p, styleNumber: sn } : p));
    }
    window.addEventListener("techpack-saved", onSaved);
    return () => window.removeEventListener("techpack-saved", onSaved);
  }, []);

  const topBar = <TopBar lang={lang} onLangChange={changeLang} onAvatarClick={() => setAccountOpen(true)} email={email} userColor={userColor} avatarUrl={avatarUrl} displayName={displayName} />;

  const accountSidebar = accountOpen ? (
    <AccountSidebar onClose={() => setAccountOpen(false)} lang={lang} onLangChange={changeLang} email={email} userColor={userColor} avatarUrl={avatarUrl} onAvatarChange={setAvatarUrl} displayName={displayName} onDisplayNameChange={setDisplayName} creditsUsed={creditsUsed} />
  ) : null;

  const SHELL: React.CSSProperties = { display:"flex", flexDirection:"column", height:"100vh", overflow:"hidden", background:"var(--bg)", fontFamily:"var(--font)" };
  const BODY:  React.CSSProperties = { display:"flex", flex:1, overflow:"hidden" };
  const LEFT:  React.CSSProperties = { width:460, minWidth:420, flexShrink:0, borderRight:"1px solid var(--border)", display:"flex", flexDirection:"column", overflow:"hidden", background:"var(--bg)" };
  const RIGHT: React.CSSProperties = { flex:1, display:"flex", flexDirection:"column", overflow:"hidden" };

  if (conn && selectedPack) {
    return (
      <StudioProvider key={selectedPack.id} techPack={conn.techPack} provider={conn.provider} userId={userId} userName={userName} userTimezone={Intl.DateTimeFormat().resolvedOptions().timeZone} userRole={selectedPack.isOwner === true ? "owner" : (selectedPack.memberRole ?? "collaborator")} userOrigin="studio" roomId={selectedPack.id} initialLanguage={lang}>
        <div style={SHELL}>
          {sessionExpired && <SessionExpiredToast />}
          <AccessDenialToast lang={lang} />
          {topBar}
          <div style={BODY}>
            <div style={LEFT}><AwarenessPanel /></div>
            <div style={RIGHT}>
              <EditorHeader pack={selectedPack} lang={lang} onBack={goBack} onStatusChange={s => handleStatusChange(selectedPack.id, s)} onExportCredit={consumeCredit} onSubmit={consumeCredit} />
              <div className="sf-form-scroll"><ErrorBoundary><TechPackForm /></ErrorBoundary></div>
            </div>
          </div>
          {accountSidebar}
        </div>
      </StudioProvider>
    );
  }

  if (pendingPack) {
    return (
      <div style={SHELL}>
        <AccessDenialToast lang={lang} />
        {topBar}
        <div style={BODY}>
          {/* E-3 (item 5): during the pack-load transition show an empty left
              panel — NOT the landing ChatPanel — so the previous view's chat
              doesn't flash before the pack's AwarenessPanel mounts. The editor
              chat itself is already keyed by selectedPack.id (StudioProvider). */}
          <div style={LEFT} />
          <div style={RIGHT}>
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"0 16px", height:44, flexShrink:0, borderBottom:"1px solid var(--border)", background:"var(--bg)" }}>
              <button onClick={() => { connRef.current?.provider.destroy(); connRef.current = null; setPendingPack(null); setConn(null); setSelectedPack(null); setActiveStyleId(null); }} style={{ ...BTN, color:"var(--text-secondary)", fontSize:11, padding:"4px 8px", letterSpacing:0 }}>{ui("back", lang)}</button>
              <span style={{ fontFamily:"'Space Mono', monospace", fontSize:11, color:"var(--text-secondary)", letterSpacing:"0.08em" }}>{pendingPack.styleNumber || ui("new_pack", lang)} -- Loading...</span>
            </div>
          </div>
        </div>
        {accountSidebar}
      </div>
    );
  }

  return (
    <div style={SHELL}>
      <AccessDenialToast lang={lang} />
      {topBar}
      <div style={BODY}>
        <div style={LEFT}><ChatPanel lang={lang} activeFolderId={activeFolderId} /></div>
        <div style={RIGHT}>
          {packsLoading
            ? <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1, color:"var(--text-muted)", fontSize:11, fontFamily:"var(--font)", letterSpacing:"0.1em" }}>LOADING...</div>
            : <FileList packs={packs} initialFolders={initialFolders} onSelect={selectPack} onNew={createNewPack} onDelete={deletePack} onImport={importPack} lang={lang} activeFolderId={activeFolderId} onActiveFolderChange={setActiveFolderId} />
          }
        </div>
      </div>
      {accountSidebar}
    </div>
  );
}

// ---------------------------------------------------------------------------
// App -- provider tree + default export
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <ErrorBoundary>
      <DegradedModeBanner />
      <WorkspaceProvider>
        <ActiveObjectProvider>
          <ThreadProvider>
            <ActivityProvider>
              <FileAssetListener />
              <FieldPatchQueue />
              <AppInner />
            </ActivityProvider>
          </ThreadProvider>
        </ActiveObjectProvider>
      </WorkspaceProvider>
    </ErrorBoundary>
  );
}
